import {CalendarDays, Eye} from 'lucide-react'
import {useMemo} from 'react'
import type {LogRecord} from './types'
import {formatLogDate} from './logUtils'
import {getLogTypeColor, LogEmptyState, LogTypeBadge, LogUser} from './LogTable'

interface LogTimelineProps {
  data: LogRecord[]
  onViewDetails: (item: LogRecord) => void
  onUserSelect: (userId: number | null) => void
}

const LogTimeline = ({data, onViewDetails, onUserSelect}: LogTimelineProps) => {
  const groups = useMemo(() => {
    const byDay = new Map<string, LogRecord[]>()
    data.forEach((item) => {
      // Dates represent the wall-clock value already stored by the system.
      const day = formatLogDate(item.created_at).split(' ')[0]
      const records = byDay.get(day) || []
      records.push(item)
      byDay.set(day, records)
    })
    return Array.from(byDay, ([day, records]) => ({day, records}))
  }, [data])

  if (!data.length) return <LogEmptyState />

  return (
    <div className='logs-timeline'>
      {groups.map(({day, records}) => (
        <section className='logs-timeline-group' key={day} aria-label={`Atividades de ${day}`}>
          <div className='logs-timeline-day'>
            <CalendarDays size={16} aria-hidden='true' />
            <h3>{day}</h3>
            <span className='logs-timeline-day-count'>
              <strong>{records.length.toLocaleString('pt-BR')}</strong>{' '}
              {records.length === 1 ? 'registro nesta página' : 'registros nesta página'}
            </span>
          </div>
          <ol className='logs-timeline-list'>
            {records.map((item) => {
              const formattedDate = formatLogDate(item.created_at)
              return (
                <li className='logs-timeline-item' key={item.id_log}>
                  <time
                    className='logs-timeline-time'
                    dateTime={formattedDate !== '—' ? item.created_at.replace(' ', 'T') : undefined}
                    aria-label={formattedDate}
                  >
                    {formattedDate.split(' ')[1] || '—'}
                  </time>
                  <span
                    className={`logs-timeline-dot logs-timeline-dot--${getLogTypeColor(item.type)}`}
                    aria-hidden='true'
                  />
                  <article className='logs-timeline-card' aria-label={`Registro ${item.id_log}`}>
                    <div className='logs-timeline-top'>
                      <LogUser item={item} onUserSelect={onUserSelect} />
                      <LogTypeBadge type={item.type} />
                    </div>
                    <p className='logs-timeline-description'>
                      {item.name || 'Atividade sem descrição'}
                    </p>
                    <div className='logs-timeline-bottom'>
                      <span className='logs-record-id'>Registro #{item.id_log}</span>
                      <button
                        type='button'
                        className='logs-details-button'
                        onClick={() => onViewDetails(item)}
                        aria-label={`Ver detalhes do registro ${item.id_log}`}
                      >
                        <Eye size={15} aria-hidden='true' />
                        Detalhes
                      </button>
                    </div>
                  </article>
                </li>
              )
            })}
          </ol>
        </section>
      ))}
    </div>
  )
}

export default LogTimeline
